/**
 * Where the API lives.
 *
 * Deliberately NOT read from `import.meta.env`: this package must not know
 * which bundler is compiling it, and declaring ambient `ImportMetaEnv` types
 * here would merge with the consuming app's `vite/client` types. Each app calls
 * `configureApiClient` once from its entry point instead.
 *
 * The default of '' means same-origin relative URLs, which is what dev wants:
 * the Vite proxy forwards /v1 and /health to the API, so there is no CORS and
 * no preflight for the custom identity headers.
 */
import { dropDevIdentity } from './identity.js';

let baseUrl = '';

/**
 * The dev identity switcher is OFF unless an app explicitly turns it on.
 *
 * It is a "become anyone" control: its option list comes from
 * `GET /v1/dev/identities`, which uses an admin pool and bypasses RLS to
 * enumerate every membership in every tenant. The API already gates that route
 * twice (unregistered when `NODE_ENV=production`, and absent without
 * `DATABASE_URL_ADMIN`) — but a dev server reachable on a network is not
 * production, so those two gates do not cover it. This is the third gate, and
 * the only one the FRONT END owns: default-off, opt-in per app, so a stack
 * someone exposed for a demo does not hand out identities.
 *
 * Turn it on with `VITE_DEV_IDENTITIES=1` in your shell (never in the generated
 * `.env`, which is committed-adjacent and shared).
 */
let devIdentitiesEnabled = false;

export function configureApiClient(opts: { baseUrl?: string; devIdentities?: boolean }): void {
  if (opts.baseUrl !== undefined) baseUrl = opts.baseUrl.replace(/\/+$/, '');
  if (opts.devIdentities !== undefined) {
    devIdentitiesEnabled = opts.devIdentities;
    // Gating the switcher has to revoke what it already wrote, or a dev pick
    // left in localStorage keeps rendering "Signed in as …" to someone who
    // never signed in. A session-derived identity survives untouched.
    if (!devIdentitiesEnabled) dropDevIdentity();
  }
}

export function getApiBaseUrl(): string {
  return baseUrl;
}

/** Whether this app opted into the dev identity switcher. Default false. */
export function devIdentitiesAllowed(): boolean {
  return devIdentitiesEnabled;
}
